/**
 * Meta Pixel, loaded only on the marketing consent given on fluenceur.com.
 *
 * The dashboard asks for no advertising consent of its own: the public site's banner
 * writes its choice to `cookie_consent` on `.fluenceur.com`, so this subdomain reads
 * the very answer the visitor gave there. No grant on the site, no pixel here. The
 * product analytics consent (`analytics-choice.ts`) covers another purpose and is
 * never read for this one.
 *
 * Consent is read again before every event, because the visitor can withdraw it on the
 * site in another tab while this one stays open.
 */

const SITE_CONSENT_COOKIE = 'cookie_consent';
/** Must follow `CONSENT_VERSION` of the public site: an older choice is asked again there. */
const SITE_CONSENT_VERSION = 2;
const PIXEL_SCRIPT = 'https://connect.facebook.net/en_US/fbevents.js';

/**
 * `Subscribe` is not here on purpose: fluenceur.com already sends it for its newsletter, so a
 * paid plan goes out as `Purchase` and the two never mix in one optimisation.
 */
export type MetaPixelEvent = 'CompleteRegistration' | 'Lead' | 'StartTrial' | 'Purchase';
export interface MetaPixelParams {
  value?: number;
  currency?: string;
  content_name?: string;
}

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};
declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export function hasSiteMarketingConsent(): boolean {
  const raw = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${SITE_CONSENT_COOKIE}=`))
    ?.slice(SITE_CONSENT_COOKIE.length + 1);
  if (!raw) return false;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    return (
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed as { v?: unknown }).v === SITE_CONSENT_VERSION &&
      (parsed as { marketing?: unknown }).marketing === true
    );
  } catch {
    return false;
  }
}

/** Meta's base snippet, without its PageView and without the `<noscript>` image. */
function installSnippet() {
  if (window.fbq) return;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  const script = document.createElement('script');
  script.async = true;
  script.src = PIXEL_SCRIPT;
  document.head.appendChild(script);
}

export function createMetaPixel(pixelId: string) {
  let initialised = false;

  function ready(): boolean {
    if (!pixelId || !hasSiteMarketingConsent()) return false;
    if (!initialised) {
      installSnippet();
      // No automatic button clicks nor page metadata: dashboard pages show customer data,
      // and only the events below are meant to leave for Meta.
      window.fbq?.('set', 'autoConfig', false, pixelId);
      window.fbq?.('init', pixelId);
      initialised = true;
    }
    return true;
  }

  return {
    /** Load the pixel at start-up so it reads the `_fbp` / `_fbc` cookies before any event. */
    boot() {
      try {
        ready();
      } catch {
        // A blocked or broken pixel never affects the dashboard.
      }
    },
    /** `eventId` lets Meta deduplicate the event against a later Conversions API copy. */
    track(event: MetaPixelEvent, params?: MetaPixelParams, eventId?: string) {
      try {
        if (!ready()) return;
        if (eventId) window.fbq?.('track', event, params ?? {}, { eventID: eventId });
        else if (params) window.fbq?.('track', event, params);
        else window.fbq?.('track', event);
      } catch {
        // Same as above: advertising measurement is never worth a failed user action.
      }
    },
  };
}

export const metaPixel = createMetaPixel(import.meta.env.VITE_META_PIXEL_ID ?? '');
